/**
 * BAB Report Table (YTD) — read-only port of bab-process-app-v2's BabReportTable,
 * rebuilt with inline Tailwind (reporting_app has no UI-kit Table component).
 *
 * Double-click a Range subtotal to expand/collapse the detail lines underneath it.
 * IBCS delta coloring: favorable = dark blue, unfavorable = red.
 */
import { useState, useCallback, useMemo } from 'react';
import { ChevronRight, ChevronDown, MessageSquare, HelpCircle } from 'lucide-react';
import type { BabLineResult, AmountScale, SignMode, BabCommentCount, BabQueryCount } from '../../types/babReport';
import type { BuTheme } from '../../utils/buTheme';
import { formatScaled, formatPercent, getDeltaColorClass, applySign } from '../../utils/babFormat';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { ColumnResizeHandle } from '../ui/ColumnResizeHandle';

/**
 * The table is `table-fixed`, so these widths actually hold and a long line name is clipped
 * instead of pushing the number columns around. Anything the user drags wins and is kept in
 * localStorage; the defaults below only apply until the first drag.
 *
 * The two badge columns are fixed at icon width and deliberately not resizable — there is
 * nothing in them to reveal.
 */
const BADGE_COL_WIDTH = 40;

const COLUMNS = [
    { key: 'line', label: 'Line', width: 420, align: 'left' as const },
    { key: 'actualYTD', label: 'Actual YTD', width: 130, align: 'right' as const },
    { key: 'budgetYTD', label: 'Budget YTD', width: 130, align: 'right' as const },
    { key: 'deltaBudget', label: 'Delta Budget', width: 130, align: 'right' as const },
    { key: 'deltaBudgetPct', label: 'Delta %', width: 90, align: 'right' as const },
    { key: 'priorYear', label: 'Prior Year', width: 130, align: 'right' as const },
    { key: 'deltaPY', label: 'Delta PY', width: 130, align: 'right' as const },
    { key: 'deltaPYPct', label: 'Delta %', width: 90, align: 'right' as const },
];

const EMPTY_COMMENT_COUNT: BabCommentCount = { total: 0, line: 0, account: 0, booking: 0 };

/** A row's badge: its own comments plus whatever it inherited from collapsed lines below. */
export interface RowCommentCount extends BabCommentCount {
    rolledUp: number;
    sourceLineCodes: string[];
}

const EMPTY_ROW_COUNT: RowCommentCount = { ...EMPTY_COMMENT_COUNT, rolledUp: 0, sourceLineCodes: [] };

/** Query badge counts for one row, incl. what rolled up from collapsed lines below it. */
export interface RowQueryCount extends BabQueryCount {
    sourceLineCodes: string[];
}

const EMPTY_ROW_QUERY_COUNT: RowQueryCount = { total: 0, open: 0, overdue: 0, sourceLineCodes: [] };

/** "2 queries, 1 still open, 1 overdue" — the badge must explain its own colour. */
function queryTitle(count: RowQueryCount): string {
    if (count.total === 0) return 'Ask a question about this line';
    const parts = [`${count.total} quer${count.total === 1 ? 'y' : 'ies'}`];
    if (count.open > 0) parts.push(`${count.open} still open`);
    if (count.overdue > 0) parts.push(`${count.overdue} overdue`);
    const own = parts.join(', ');
    return count.sourceLineCodes.length > 0
        ? `${own} — incl. collapsed lines below (${count.sourceLineCodes.join(', ')})`
        : own;
}

function getChildLineCodes(parent: BabLineResult, all: BabLineResult[]): Set<string> {
    const children = new Set<string>();
    if (parent.operator === 'Range' && parent.calc1 && parent.calc2) {
        const c1 = all.find(l => l.lineCode === parent.calc1);
        const c2 = all.find(l => l.lineCode === parent.calc2);
        if (!c1 || !c2) return children;
        for (const line of all) {
            if (line.lineOrder >= c1.lineOrder && line.lineOrder <= c2.lineOrder) children.add(line.lineCode);
        }
    }
    return children;
}

function getExpandableLines(all: BabLineResult[]): Set<string> {
    const expandable = new Set<string>();
    for (const line of all) {
        if (line.operator === 'Range' && line.calc1 && line.calc2) {
            if (getChildLineCodes(line, all).size > 0) expandable.add(line.lineCode);
        }
    }
    return expandable;
}

interface RowProps {
    line: BabLineResult;
    scale: AmountScale;
    signMode: SignMode;
    isExpandable: boolean;
    isExpanded: boolean;
    onToggleExpand: (lineCode: string) => void;
    onLineClick?: (line: BabLineResult) => void;
    commentCount?: RowCommentCount;
    onCommentClick?: (line: BabLineResult, rolledUpLineCodes?: string[]) => void;
    queryCount?: RowQueryCount;
    onQueryClick?: (line: BabLineResult, rolledUpLineCodes?: string[]) => void;
    theme?: BuTheme;
    isHighlighted?: boolean;
}

/** "2 comments (1 on the line, 1 on a booking)" — makes the bubbled-up count self-explanatory. */
function commentTitle(count: RowCommentCount): string {
    if (count.total === 0) return 'View comments';
    const parts: string[] = [];
    if (count.line > 0) parts.push(`${count.line} on the line`);
    if (count.account > 0) parts.push(`${count.account} on account${count.account === 1 ? '' : 's'}`);
    if (count.booking > 0) parts.push(`${count.booking} on booking${count.booking === 1 ? '' : 's'}`);
    const own = `${count.total} comment${count.total === 1 ? '' : 's'} (${parts.join(', ')})`;
    return count.rolledUp > 0
        ? `${own} — ${count.rolledUp} from collapsed line${count.rolledUp === 1 ? '' : 's'} below (${count.sourceLineCodes.join(', ')})`
        : own;
}

function BabReportRow({ line, scale, signMode, isExpandable, isExpanded, onToggleExpand, onLineClick, commentCount = EMPTY_ROW_COUNT, onCommentClick, queryCount = EMPTY_ROW_QUERY_COUNT, onQueryClick, theme, isHighlighted }: RowProps) {
    const isClickable = line.operator === 'Sum' && !!onLineClick;
    const isKpiAccent = line.isRunningTotal && !!theme;

    const rowClasses = [
        !isKpiAccent && line.isRunningTotal ? 'bg-gray-100 font-semibold' : '',
        !isKpiAccent && line.isSubTotal ? 'font-semibold bg-gray-50' : '',
        isKpiAccent ? 'font-bold' : '',
        isClickable ? 'cursor-pointer hover:bg-gray-100' : '',
        isExpandable ? 'cursor-pointer' : '',
        isHighlighted ? 'ring-2 ring-inset ring-blue-500' : '',
    ].filter(Boolean).join(' ');

    const kpiRowStyle = isKpiAccent
        ? { backgroundColor: theme!.primary, color: theme!.textOnAccent === 'text-gray-900' ? '#111827' : '#ffffff' }
        : undefined;

    const indentPx = (line.textIndent * 16) + 24;
    const chevronWidth = 20;
    const firstCellStyle: React.CSSProperties = {
        paddingLeft: `${isExpandable ? indentPx - chevronWidth : indentPx}px`,
        ...(kpiRowStyle ? { color: kpiRowStyle.color } : {}),
    };

    // The colour is computed from the NATIVE delta on purpose — `formatAmount` may flip the
    // printed sign, but "favorable" must not flip with it.
    const deltaStyle = (delta: number, signage: number) =>
        isKpiAccent ? { style: { color: kpiRowStyle!.color } } : { className: getDeltaColorClass(delta, signage) };
    const secondary = isKpiAccent ? { style: { color: kpiRowStyle!.color } } : { className: 'text-gray-600' };
    const budgetDelta = deltaStyle(line.deltaBudget, line.signage);
    const pyDelta = deltaStyle(line.deltaPY, line.signage);
    const numCell = 'px-3 py-1.5 text-right tabular-nums';
    const formatAmount = (amount: number) => formatScaled(applySign(amount, line.signage, signMode), scale);

    return (
        <tr
            className={rowClasses}
            style={kpiRowStyle}
            onClick={isClickable ? () => onLineClick!(line) : undefined}
            onDoubleClick={isExpandable ? () => onToggleExpand(line.lineCode) : undefined}
        >
            {/* `truncate` because the table is table-fixed: without it a long name wraps the
                row to two lines instead of clipping. The full text stays in the tooltip. */}
            <td
                className="px-3 py-1.5 font-medium select-none truncate"
                style={firstCellStyle}
                title={line.lineCode ? `${line.lineName} (${line.lineCode})` : line.lineName}
            >
                {isExpandable && (
                    <span className="inline-flex items-center mr-0.5 align-middle" style={{ width: `${chevronWidth}px`, color: isKpiAccent ? 'rgba(255,255,255,0.7)' : undefined }}>
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </span>
                )}
                {line.lineName}
                {line.lineCode && !isKpiAccent && (
                    <span className="ml-2 text-xs text-gray-400">({line.lineCode})</span>
                )}
            </td>
            <td className={numCell} style={kpiRowStyle ? { color: kpiRowStyle.color } : undefined}>{formatAmount(line.actualYTD)}</td>
            <td className={`${numCell} ${secondary.className || ''}`} style={secondary.style}>{formatAmount(line.budgetYTD)}</td>
            <td className={`${numCell} ${budgetDelta.className || ''}`} style={budgetDelta.style}>{formatAmount(line.deltaBudget)}</td>
            <td className={`${numCell} ${budgetDelta.className || ''}`} style={budgetDelta.style}>{formatPercent(line.deltaBudgetPct)}</td>
            <td className={`${numCell} ${secondary.className || ''}`} style={secondary.style}>{formatAmount(line.priorYearYTD)}</td>
            <td className={`${numCell} ${pyDelta.className || ''}`} style={pyDelta.style}>{formatAmount(line.deltaPY)}</td>
            <td className={`${numCell} ${pyDelta.className || ''}`} style={pyDelta.style}>{formatPercent(line.deltaPYPct)}</td>
            {onCommentClick && (
                <td className="px-1 py-1.5 text-center w-10">
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onCommentClick(line, commentCount.sourceLineCodes); }}
                        className="relative inline-flex items-center justify-center p-1 rounded hover:bg-black/10"
                        title={commentTitle(commentCount)}
                    >
                        <MessageSquare size={15} style={isKpiAccent ? { color: kpiRowStyle!.color } : undefined} className={commentCount.total > 0 ? 'text-blue-600' : 'text-gray-400'} />
                        {commentCount.total > 0 && (
                            // Lighter badge when everything on it was inherited from collapsed lines —
                            // nothing was written on this row itself.
                            <span className={`absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5 rounded-full text-white text-[9px] leading-[14px] font-semibold ${
                                commentCount.rolledUp === commentCount.total ? 'bg-blue-400' : 'bg-blue-600'
                            }`}>
                                {commentCount.total}
                            </span>
                        )}
                    </button>
                </td>
            )}
            {onQueryClick && (
                <td className="px-1 py-1.5 text-center w-10">
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onQueryClick(line, queryCount.sourceLineCodes); }}
                        className="relative inline-flex items-center justify-center p-1 rounded hover:bg-black/10"
                        title={queryTitle(queryCount)}
                    >
                        <HelpCircle
                            size={15}
                            style={isKpiAccent ? { color: kpiRowStyle!.color } : undefined}
                            className={queryCount.total > 0
                                ? (queryCount.overdue > 0 ? 'text-red-600' : queryCount.open > 0 ? 'text-amber-600' : 'text-gray-500')
                                : 'text-gray-400'}
                        />
                        {queryCount.total > 0 && (
                            // Red the moment something is late, amber while it is merely open,
                            // grey once everything on this row has been settled.
                            <span className={`absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5 rounded-full text-white text-[9px] leading-[14px] font-semibold ${
                                queryCount.overdue > 0 ? 'bg-red-600'
                                    : queryCount.open > 0 ? 'bg-amber-500' : 'bg-gray-400'
                            }`}>
                                {queryCount.total}
                            </span>
                        )}
                    </button>
                </td>
            )}
        </tr>
    );
}

interface BabReportTableProps {
    lines: BabLineResult[];
    maxViewLevel?: number;
    scale?: AmountScale;
    signMode?: SignMode;
    onLineClick?: (line: BabLineResult) => void;
    commentCounts?: Record<string, BabCommentCount>;
    /** `rolledUpLineCodes` are the collapsed lines whose comments this row is standing in for. */
    onCommentClick?: (line: BabLineResult, rolledUpLineCodes?: string[]) => void;
    queryCounts?: Record<string, BabQueryCount>;
    onQueryClick?: (line: BabLineResult, rolledUpLineCodes?: string[]) => void;
    theme?: BuTheme;
    /** Line briefly ringed after jumping here from a comment. */
    highlightedLineCode?: string | null;
}

export function BabReportTable({ lines, maxViewLevel = 0, scale = 'thousands', signMode = 'bab', onLineClick, commentCounts, onCommentClick, queryCounts, onQueryClick, theme, highlightedLineCode }: BabReportTableProps) {
    const [expandedLines, setExpandedLines] = useState<Set<string>>(new Set());
    const { widthOf, totalWidthOf, resizingKey, startResize, resetWidth } =
        useResizableColumns(COLUMNS, 'bab-ytd-col-widths');

    const toggleExpand = useCallback((lineCode: string) => {
        setExpandedLines(prev => {
            const next = new Set(prev);
            if (next.has(lineCode)) next.delete(lineCode); else next.add(lineCode);
            return next;
        });
    }, []);

    const expandableLines = useMemo(() => getExpandableLines(lines), [lines]);

    const childToParents = useMemo(() => {
        const map = new Map<string, string[]>();
        for (const line of lines) {
            if (expandableLines.has(line.lineCode)) {
                for (const child of getChildLineCodes(line, lines)) {
                    const parents = map.get(child) || [];
                    parents.push(line.lineCode);
                    map.set(child, parents);
                }
            }
        }
        return map;
    }, [lines, expandableLines]);

    const visibleLines = useMemo(() => {
        return lines.filter(line => {
            const vl = line.viewLevel ?? 0;
            if (vl >= 9) return false;
            const passesViewLevel = maxViewLevel === 0 || vl <= maxViewLevel;
            const parents = childToParents.get(line.lineCode);
            if (parents && parents.length > 0 && !passesViewLevel) {
                return parents.every(p => expandedLines.has(p));
            }
            return passesViewLevel;
        });
    }, [lines, maxViewLevel, expandedLines, childToParents]);

    /**
     * A comment on a collapsed line must not disappear: its badge rolls up to the nearest
     * VISIBLE subtotal above it, and returns to its own row as soon as that subtotal is
     * expanded. `rolledUp` counts what was inherited, and `sourceLineCodes` are the hidden
     * lines behind it, so the panel can show exactly those comments.
     */
    /**
     * Where each line's badge ends up: itself when visible, otherwise the nearest visible
     * subtotal above it. Shared by the comment and the query badge so the two can never
     * disagree about which row is standing in for a collapsed line.
     */
    const hostOfLine = useMemo(() => {
        const visibleCodes = new Set(visibleLines.map(l => l.lineCode));
        // Most specific first: the subtotal spanning the fewest lines is the closest ancestor.
        const spanOf = new Map<string, number>();
        for (const code of expandableLines) {
            const parent = lines.find(l => l.lineCode === code);
            spanOf.set(code, parent ? getChildLineCodes(parent, lines).size : Number.MAX_SAFE_INTEGER);
        }
        return (code: string): { host: string | null; rolled: boolean } => {
            if (visibleCodes.has(code)) return { host: code, rolled: false };
            const host = (childToParents.get(code) ?? [])
                .filter(p => visibleCodes.has(p))
                .sort((a, b) => (spanOf.get(a) ?? 0) - (spanOf.get(b) ?? 0))[0];
            // Nothing visible to carry it — better silent than on a wrong row.
            return { host: host ?? null, rolled: true };
        };
    }, [visibleLines, childToParents, expandableLines, lines]);

    const displayCounts = useMemo(() => {
        const result: Record<string, RowCommentCount> = {};
        const ensure = (code: string) => (result[code] ??= { ...EMPTY_COMMENT_COUNT, rolledUp: 0, sourceLineCodes: [] });

        for (const [code, count] of Object.entries(commentCounts ?? {})) {
            if (count.total === 0) continue;
            const { host, rolled } = hostOfLine(code);
            if (!host) continue;

            const bucket = ensure(host);
            bucket.total += count.total;
            bucket.line += count.line;
            bucket.account += count.account;
            bucket.booking += count.booking;
            if (rolled) {
                bucket.rolledUp += count.total;
                bucket.sourceLineCodes.push(code);
            }
        }
        return result;
    }, [commentCounts, hostOfLine]);

    /** The same roll-up for queries. `open` colours the badge, `overdue` turns it red. */
    const displayQueryCounts = useMemo(() => {
        const result: Record<string, RowQueryCount> = {};
        const ensure = (code: string) => (result[code] ??= { total: 0, open: 0, overdue: 0, sourceLineCodes: [] });

        for (const [code, count] of Object.entries(queryCounts ?? {})) {
            if (count.total === 0) continue;
            const { host, rolled } = hostOfLine(code);
            if (!host) continue;

            const bucket = ensure(host);
            bucket.total += count.total;
            bucket.open += count.open;
            bucket.overdue += count.overdue;
            if (rolled) bucket.sourceLineCodes.push(code);
        }
        return result;
    }, [queryCounts, hostOfLine]);

    if (!lines || lines.length === 0) {
        return <div className="text-center py-12 text-gray-500">No data available for the selected period and filters.</div>;
    }

    const th = 'sticky top-0 z-10 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-500';
    const badgeCols = (onCommentClick ? 1 : 0) + (onQueryClick ? 1 : 0);
    const tableWidth = totalWidthOf(COLUMNS.map(c => c.key)) + badgeCols * BADGE_COL_WIDTH;

    return (
        <>
        {/* The header only stays put if THIS div is the nearest scrolling ancestor of the
            sticky <th> — i.e. it has to be bounded vertically. A plain `overflow-x-auto`
            also becomes that ancestor but never scrolls vertically, so the header would
            silently ride out of view with the page. */}
        <div className="overflow-auto max-h-[calc(100vh-300px)]">
            {/* `minWidth`, not `width`: the table fills a wide container and scrolls sideways
                in a narrow one, instead of squeezing columns back out of the widths the user set. */}
            <table className="w-full table-fixed text-[15px] border-collapse" style={{ minWidth: tableWidth }}>
                <colgroup>
                    {COLUMNS.map(c => <col key={c.key} style={{ width: widthOf(c.key) }} />)}
                    {onCommentClick && <col style={{ width: BADGE_COL_WIDTH }} />}
                    {onQueryClick && <col style={{ width: BADGE_COL_WIDTH }} />}
                </colgroup>
                <thead>
                    <tr className="border-b border-gray-200">
                        {COLUMNS.map(c => (
                            <th
                                key={c.key}
                                className={`${th} relative ${c.align === 'right' ? 'text-right' : 'text-left'}`}
                            >
                                <span className="block truncate">{c.label}</span>
                                <ColumnResizeHandle
                                    label={c.label}
                                    active={resizingKey === c.key}
                                    onStart={x => startResize(c.key, x)}
                                    onReset={() => resetWidth(c.key)}
                                />
                            </th>
                        ))}
                        {onCommentClick && <th className={`${th} text-center`}> </th>}
                        {onQueryClick && <th className={`${th} text-center`}> </th>}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {visibleLines.map(line => (
                        <BabReportRow
                            key={line.lineCode}
                            line={line}
                            scale={scale}
                            signMode={signMode}
                            isExpandable={expandableLines.has(line.lineCode)}
                            isExpanded={expandedLines.has(line.lineCode)}
                            onToggleExpand={toggleExpand}
                            onLineClick={onLineClick}
                            commentCount={displayCounts[line.lineCode] ?? EMPTY_ROW_COUNT}
                            onCommentClick={onCommentClick}
                            queryCount={displayQueryCounts[line.lineCode] ?? EMPTY_ROW_QUERY_COUNT}
                            onQueryClick={onQueryClick}
                            theme={theme}
                            isHighlighted={highlightedLineCode === line.lineCode}
                        />
                    ))}
                </tbody>
            </table>
        </div>
        <p className="mt-2 px-3 pb-2 text-xs text-gray-400">Double-click a subtotal row to expand its detail lines.</p>
        </>
    );
}
